import clsx from "clsx"
import { FC, KeyboardEvent, memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import styles from "./BattleFieldCell.module.scss"
import { IBattleFieldCellProps } from "./types"

const BattleFieldCellComponent: FC<IBattleFieldCellProps> = ({
  cell,
  onMove
}) => {
  const { t } = useTranslation("battle")
  const { token } = cell

  const archetypeLabel = token ? t(`archetype.${token.archetype}`) : ""
  const tokenLabel =
    token?.isHero === true
      ? `${archetypeLabel} · ${t("screen.you")}`
      : archetypeLabel

  const { move } = cell
  const reachable = move !== null

  const onSelect = useCallback(() => {
    if (move !== null) {
      onMove(move)
    }
  }, [move, onMove])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        onSelect()
      }
    },
    [onSelect]
  )

  return (
    <div
      className={clsx(
        styles.BattleFieldCell,
        cell.side === "ally"
          ? styles.BattleFieldCell__ally
          : styles.BattleFieldCell__enemy,
        reachable && styles.BattleFieldCell__reachable
      )}
      {...(reachable && {
        role: "button",
        tabIndex: 0,
        "aria-label": t("screen.moveHere"),
        onClick: onSelect,
        onKeyDown
      })}
    >
      {token && (
        <div
          className={clsx(
            styles.BattleFieldCell_token,
            token.isHero && styles.BattleFieldCell_token__hero
          )}
          role="img"
          aria-label={tokenLabel}
          title={tokenLabel}
        >
          <span className={styles.BattleFieldCell_archetype}>
            {archetypeLabel}
          </span>
          {token.isHero && (
            <span className={styles.BattleFieldCell_you}>
              {t("screen.you")}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export const BattleFieldCell = memo(BattleFieldCellComponent)
